import React from 'react'
import { Stack, Text } from '@chakra-ui/react'
import { CashJournalValues } from './Types'
import { DistributionReasonInfo, validContributions } from '@Types'
import { buildIraContributionOptions, buildIraDistributionOptions } from '../../utils/optionBuilders'
import { useFormatMessage } from '../../utils/intlHelpers'
import { Checkbox, FormLabel, LabeledSelect, RadioGroup, Radio } from '@apex/component-library'

import { FormikErrors, FormikTouched, useFormikContext } from 'formik'
import { TaxWithholdingFields } from '../IraDistributionForm/TaxWithholdingFields'
import { useWaiverStates } from '../../api'
import { PREVIOUS, CURRENT } from '../IraContributionForm/types'
import { DateTime } from 'luxon'

export interface Props {
  distributionReason?: DistributionReasonInfo[]
  contributionReason?: validContributions[]
  errors?: FormikErrors<CashJournalValues>
  touched?: FormikTouched<CashJournalValues>
  saveData: (fieldName: string, value: string) => void
  showFedAndStateTax?: boolean
}
export const IratoIraForm = ({
  distributionReason,
  contributionReason,
  touched,
  errors,
  saveData,
  showFedAndStateTax = false
}: Props) => {
  const formatMessage = useFormatMessage()
  const formik = useFormikContext<any>()
  const displayOptOutForm = useWaiverStates()
  const selectedPreviousYearAllowed = contributionReason?.find(
    (type) => type.reason === formik.values.contributionReason
  )?.previousYearContributionAllowed
  const contributionDeadline = contributionReason?.find((type) => type.reason === formik.values.contributionReason)
    ?.previousYearDeadline
  const isPastDeadline = contributionDeadline
    ? DateTime.local().startOf('day') > DateTime.fromFormat(contributionDeadline, 'yyyy-MM-dd')
    : false
  const displayContributionYearOption = selectedPreviousYearAllowed && !isPastDeadline

  return (
    <>
      <Text as='h6' className='test-id-IRA-IRA-header'>
        {formatMessage('ira.ira.header')}
      </Text>
      <LabeledSelect
        className='test-id-IRA-IRA-dist-reason'
        name='distributionReason'
        label={'From Account: Distribution Reason'}
        isRequired
        onChange={(e) => {
          saveData('distributionReason', e.target.value)
        }}
        isInvalid={!!touched?.distributionReason && !!errors?.distributionReason}
        errorMessage={errors?.distributionReason}
      >
        <option></option>
        {distributionReason &&
          buildIraDistributionOptions(distributionReason)?.map((option, index) => (
            <option key={`${index}-${option.value}`} value={option.value}>
              {option.text}
            </option>
          ))}
      </LabeledSelect>

      <LabeledSelect
        className='test-id-IRA-IRA-cont-reason'
        name='contributionReason'
        label={'To Account: Contribution Reason'}
        isRequired
        onChange={(e) => {
          saveData('contributionReason', e.target.value)
        }}
        isInvalid={!!touched?.contributionReason && !!errors?.contributionReason}
        errorMessage={errors?.contributionReason}
      >
        <option></option>
        {contributionReason &&
          buildIraContributionOptions(contributionReason)?.map((option, index) => (
            <option key={`${index}-${option.value}`} value={option.value}>
              {option.text}
            </option>
          ))}
      </LabeledSelect>
      {formik.values.contributionReason && displayContributionYearOption && (
        <Stack>
          <FormLabel fontWeight='semibold'>Contribution Year *</FormLabel>
          <RadioGroup
            name='contributionYear'
            onChange={(val) => {
              formik.setFieldValue('contributionYear', val)
            }}
            value={formik.values.contributionYear}
          >
            <Radio value={CURRENT}>Current Year</Radio>
            <Radio value={PREVIOUS}>Previous Year</Radio>
          </RadioGroup>
        </Stack>
      )}
      {formik.values.contributionReason && !displayContributionYearOption && (
        <Stack spacing={2}>
          <FormLabel fontWeight='semibold'>Contribution Year *</FormLabel>
          <Text>The contribution will be applied to the current year.</Text>
        </Stack>
      )}
      <TaxWithholdingFields
        formikSetValues={formik.setValues}
        formikValues={formik.values}
        formikBlur={formik.handleBlur}
        formikTouched={formik.touched}
        setFieldValue={formik.setFieldValue}
        setTouched={formik.setTouched}
        errors={formik.errors}
        federalTaxWithholdingEnabled={showFedAndStateTax}
        stateTaxWithholdingEnabled={showFedAndStateTax}
      />
      {displayOptOutForm && (
        <Checkbox
          name='optOutFormProvided'
          display='inline'
          data-testid='optOutFormProvided'
          isChecked={formik.values.optOutFormProvided}
          onChange={(e) => {
            formik.setFieldValue('optOutFormProvided', e.target.checked)
          }}
        >
          {`Opt Out Form Provided`}
        </Checkbox>
      )}
    </>
  )
}
